import { React, useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from 'framer-motion';

import "./index.scss";

import SideNav from "../../components/PostLogin/SideNav";
import ProfileAvatar from '../../components/ui/ProfileAvatar';
import SideModal from "../../components/ui/SideModal";
import { toaster } from "../../components/ui/Toaster";
import SkeletonLoader from "../../components/ui/SkeletonLoader";

import DomainTab from "../../components/PostLogin/OrganizationDetails/DomainTab";
import UsersTab from "../../components/PostLogin/OrganizationDetails/UsersTab";
import OrganizationTab from "../../components/PostLogin/UserDetails/OrganizationTab";
import EditSubscriptionModal from "../../components/PostLogin/CustomerDetails/EditSubscriptionModal";
import ExtendSubscriptionModal from "../../components/PostLogin/CustomerDetails/ExtendSubscriptionModal";
import CancelSubscriptionModal from "../../components/PostLogin/CustomerDetails/CancelSubscriptionModal";
import AddCustomerModal from "../../components/PostLogin/Cutomers/AddCustomer";
import DeleteCustomerModal from "../../components/PostLogin/CustomerDetails/DeleteCustomerModal";

import { editIcon, deleteIcon, subscriptionTypeDemoIcon, subscriptionDownArrow, headerArrowRightIcon } from "../../utils/icons.js";
import { getCustomerImageUrl, getUserImageUrl, formatDate, formatNumber } from "../../utils/util.js";

import { useSelector, useDispatch } from 'react-redux';
import { getCustomerDetails, resetCustomerDetailResponse, resetUpdateCustomerDetailsResponse, resetCustomerListResponse, resetDeleteCustomerResponse } from '../../store/actions/customerAction.js';

const CustomerDetails = () => {

    const customerDetailResponse = useSelector((state) => state.customer?.customerDetailResponse);
    const deleteCustomerResponse = useSelector((state) => state.customer?.deleteCustomerResponse);

    const updateCustomerResponse = useSelector((state) => state.customer?.updateCustomerResponse);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const { customerId } = useParams();

    const navigate = useNavigate();

    const editSubscriptionWrapperRef = useRef(null);

    const [activeTab, setActiveTab] = useState("domain");
    const [customerDetails, setCustomerDetails] = useState(null);
    const [showSubscriptionOption, setShowSubscriptionOption] = useState(false);
    const [showEditSubscriptionModal, setShowEditSubscriptionModal] = useState(false);
    const [showExtendSubscriptionModal, setShowExtendSubscriptionModal] = useState(false);
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [showCancelSubscription, setShowCancelSubscriptionModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteCustomerModal, setShowDeleteCustomerModal] = useState(false);

    const backToMainPage = () =>{
        setShowAddCustomerModal(false)
        if(updateCustomerResponse?.detail?.status === "success") {
            dispatch(resetCustomerDetailResponse())
            dispatch(resetUpdateCustomerDetailsResponse())
            setIsLoading(true)
            fetchCustomerDetails()
        }      
    }

    const fetchCustomerDetails = useCallback(()=>{
        let obj = {
            customer_id: customerId
        }
        setIsLoading(true)
        dispatch(getCustomerDetails(obj))
    },[dispatch, customerId])

    useEffect(()=>{
        document.addEventListener("mousedown", handleClickOutside);

        function handleClickOutside(event) {
            if (editSubscriptionWrapperRef.current && !editSubscriptionWrapperRef.current.contains(event.target)) {
                if(showSubscriptionOption){
                    setShowSubscriptionOption(false)
                }
            }
        
        }
    },[showSubscriptionOption])

    const backToCustomerPage = () => {
        dispatch(resetCustomerDetailResponse());
        navigate('/customers')
    }

    useEffect(()=>{
        fetchCustomerDetails()
    },[fetchCustomerDetails])

    useEffect(()=>{
        if(customerDetailResponse !== null) {
            setIsLoading(false)
            setCustomerDetails(customerDetailResponse)
            dispatch(resetCustomerDetailResponse());
        }
    },[customerDetailResponse, dispatch])

    useEffect(()=>{
        if(deleteCustomerResponse != null) {
            if(deleteCustomerResponse?.detail?.status === "success") {
                toaster.addToast(deleteCustomerResponse?.detail?.description, "success")
                dispatch(resetCustomerListResponse())
                setShowDeleteCustomerModal(false)
                navigate(`/customers`)
            } else {
                setShowDeleteCustomerModal(false)
                toaster.addToast(deleteCustomerResponse?.description, "error")
            }
            dispatch(resetDeleteCustomerResponse())
        }
    },[deleteCustomerResponse, dispatch, navigate])

    return (
        <div className="organization-details-container">
            <SideNav />
            <div className="organization-page-container">
                <div className="page-header-container px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="header-left d-flex align-items-center gap-3">
                        <h1 onClick={()=>{backToCustomerPage()}}>{t('customerDetails.customers')}</h1>
                        {headerArrowRightIcon()}
                        {customerDetails !== null && <ProfileAvatar type="domain" name={customerDetails?.name} imageUrl={getCustomerImageUrl(customerDetails)} />}
                    </div>
                    <div className="header-right d-flex gap-3">
                        <button onClick={()=>{setShowAddCustomerModal(true)}}>
                            <p className="d-inline-block mx-1">{editIcon()}</p>
                            {t("organizationDetailsPage.editBtn")}
                        </button>
                        <button onClick={()=>{setShowDeleteCustomerModal(true)}}>
                            <p className="d-inline-block mx-1">{deleteIcon()}</p>
                            {t("organizationDetailsPage.deleteBtn")}
                        </button>
                    </div>
                </div>
                <div className="page-main-section p-4 cutomer-details-container">
                    <div className="organization-created d-flex justify-content-between">
                        <h4>{t('customerDetails.heading')}</h4>
                        {customerDetails !== null && <ProfileAvatar pageType={"organizationDetails"} type="profile" firstName={customerDetails?.created_by?.name} lastName={""} imageUrl={getUserImageUrl(customerDetails?.created_by)} />}
                    </div>
                    {
                        isLoading ?
                            <div className="organization-header-container p-3 my-3 rounded">
                                <SkeletonLoader type={"line"} count={3} currentPage={"customer-details"} />
                            </div>                            
                        :
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="organization-header-container p-3 my-3 rounded">                            
                                    <div className="d-flex justify-content-between">
                                        <div className="header-details d-flex gap-3">
                                            <p>{t('customerDetails.createdOn')} <span>{formatDate(customerDetails?.created_at)}</span></p>
                                            {customerDetails?.updated_at !== null && <p>{t('customerDetails.updatedOn')} <span>{formatDate(customerDetails?.updated_at)}</span></p>}
                                        </div>
                                        <div className="organization-status position-relative d-flex gap-3">                                
                                            <p className="porfolio" onClick={()=>{setShowSubscriptionOption(!showSubscriptionOption)}}>{t('customerDetails.subscription')} <span>{subscriptionTypeDemoIcon()}&nbsp;{customerDetails?.subscription?.name === "trial" ? "Demo" : "Business"}</span> {subscriptionDownArrow()}</p>
                                            {showSubscriptionOption && <div className="position-absolute subscription-edit-option-container" ref={editSubscriptionWrapperRef}>
                                                <div className="subscription-option">
                                                    <p onClick={()=>{setShowEditSubscriptionModal(true)}}>{t('customerDetails.edit')}</p>
                                                    <p onClick={()=>{setShowExtendSubscriptionModal(true)}}>{t('customerDetails.extend')}</p>
                                                    <p onClick={()=>{setShowCancelSubscriptionModal(true)}}>{t('customerDetails.cancel')}</p>
                                                </div>
                                            </div>}
                                        </div>
                                    </div>
                                    <div className="d-flex gap-3 mt-3">
                                        <div className="total-score-container rounded">
                                            <div className="users-totol-count d-flex">
                                                <p className="total-user-count">{formatNumber(customerDetails?.stats.domains?.total)}</p>
                                                <div className="user-count mx-3 mt-2">
                                                    <span className="active internal d-inline-block"></span>
                                                    <span className="disbled portfolio d-inline-block ms-1"></span>
                                                    <span className="disbled third-party d-inline-block ms-1"></span>
                                                    <p>{t("organizationDetailsPage.totalDomain")}</p>
                                                </div>
                                            </div>
                                            <div className="active-disabled-count d-flex gap-5">
                                                <p className="active-user internal"><span className="d-inline-block">{formatNumber(customerDetails?.stats.domains?.internal)}</span> Internal</p>
                                                <p className="disabled-user portfolio"><span className="d-inline-block">{formatNumber(customerDetails?.stats.domains?.portfolio)}</span> Portfolio</p>
                                            </div>
                                            <div className="active-disabled-count d-flex gap-3 mt-3">
                                                <p className="disabled-user third-party"><span className="d-inline-block">{formatNumber(customerDetails?.stats.domains?.third_party)}</span> Third Party</p>
                                            </div>
                                        </div>
                                        <div className="total-score-container rounded">
                                            <div className="users-totol-count d-flex">
                                                <p className="total-user-count">{formatNumber(customerDetails?.stats.users?.total)}</p>
                                                <div className="user-count mx-3 mt-2">
                                                    <span className="active d-inline-block mx-1"></span>
                                                    <span className="disbled d-inline-block"></span>
                                                    <p>{t("organizationDetailsPage.totalUser")}</p>
                                                </div>
                                            </div>
                                            <div className="active-disabled-count d-flex gap-3">
                                                <p className="active-user"><span className="d-inline-block">{formatNumber(customerDetails?.stats.users?.active)}</span> {t("organizationDetailsPage.activeUser")}</p>
                                                <p className="disabled-user"><span className="d-inline-block">{formatNumber(customerDetails?.stats.users?.disabled)}</span> {t("organizationDetailsPage.disabledUser")}</p>
                                            </div>
                                        </div>
                                        <div className="total-domains-container rounded">
                                            <p className="total-domain">{formatNumber(customerDetails?.stats.organizations?.total)}</p>
                                            <p className="domain-text">{t("customerDetails.totalOrganizaton")}</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                    }                        
                    <div className="organization-main-container p-3">
                        <div className="orgs-tab-container d-flex rounded">
                            <p className={`rounded text-center ${activeTab === "domain" ? "active" : ""}`} onClick={()=>setActiveTab("domain")}>{t("customerDetails.domain")}</p>
                            <p className={`rounded text-center ${activeTab === "organization" ? "active" : ""}`} onClick={()=>setActiveTab("organization")}>{t("customerDetails.organization")}</p>
                            <p className={`rounded text-center ${activeTab === "user" ? "active" : ""}`} onClick={()=>setActiveTab("user")}>{t("customerDetails.users")}</p>
                        </div>
                        {activeTab === "domain" && <DomainTab customerDetails={customerDetails} currentPageType={"customerDetails"} />}
                        {activeTab === "organization" && <OrganizationTab customerDetails={customerDetails} currentPageType={"customerDetails"}/>}
                        {activeTab === "user" && <UsersTab customerDetails={customerDetails} currentPageType={"customerDetails"} />}
                    </div>
                    <SideModal showSidePanel={showAddCustomerModal} hideSidePanel={backToMainPage}>
                        {showAddCustomerModal && <AddCustomerModal type="edit" customerDetails={customerDetails} backToMainPage={backToMainPage}/>}
                    </SideModal>
                    <EditSubscriptionModal show={showEditSubscriptionModal} customerDetails={customerDetails}  onHide={() => setShowEditSubscriptionModal(false)} />
                    <ExtendSubscriptionModal show={showExtendSubscriptionModal} customerDetails={customerDetails} onHide={() => setShowExtendSubscriptionModal(false)} />
                    <CancelSubscriptionModal show={showCancelSubscription} customerDetails={customerDetails} onHide={()=>{setShowCancelSubscriptionModal(false)}} />
                    <DeleteCustomerModal show={showDeleteCustomerModal} customerDetails={customerDetails} onHide={()=>{setShowDeleteCustomerModal(false)}} />
                </div>                
            </div>
        </div>
    )
}

export default CustomerDetails